import type { CurrencyCode, Money } from '@taler/domain'
import { negateMoney } from '@taler/domain'
import { useI18n } from 'vue-i18n'
import FxTransferRowDetail from '@/features/entries/components/FxTransferRowDetail.vue'
import { entryFields } from '../internal/draft.ts'
import { defineEntryExtension, type RegisteredExtension } from '../internal/extension.ts'

export interface FxTransferMatch {
  readonly key: string
  readonly label: string
  readonly receivedCurrency: CurrencyCode
}

export interface FxTransferDraft {
  readonly received: Money | null
}

/** A transfer between two money accounts in different currencies: the second amount is typed, never converted. */
export function useFxTransferExtension(): RegisteredExtension {
  const { t } = useI18n()
  return defineEntryExtension<FxTransferMatch, FxTransferDraft>({
    id: 'fx-transfer',
    detect: (view) => {
      const to = view.counters[0]
      if (view.kind !== 'TRANSFER' || view.currency === undefined || to?.currency === undefined)
        return undefined
      if (to.currency === view.currency) return undefined
      return { key: to.currency, label: t('entries.fx.label'), receivedCurrency: to.currency }
    },
    carries: new Set(['payee', 'memo', 'tags']),
    initialDraft: () => ({ received: null }),
    problems: (match, draft) =>
      draft.received === null
        ? [
            {
              severity: 'needs-details',
              message: t('entries.fx.needsReceived', { currency: match.receivedCurrency }),
            },
          ]
        : [],
    detail: FxTransferRowDetail,
    placement: 'inline',
    submit: {
      shape: 'postings',
      build: (_match, draft, view) => ({
        ...entryFields(view.row),
        postings: [
          { accountId: view.row.accountId!, amount: negateMoney(view.total!) },
          { accountId: view.row.splits[0]!.targetId!, amount: draft.received! },
        ],
      }),
    },
  })
}
